<template>
  <div class="cat-grupoempleado">
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>

    <div v-else-if="canAccess" class="cat-grupoempleado-content">
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[{ id: 'main', buttons: [
            { id: 'nuevo', text: 'Nuevo', icon: 'bi bi-plus', variant: 'primary', event: 'new' },
            { id: 'actualizar', text: 'Actualizar', icon: 'bi bi-arrow-clockwise', variant: 'secondary', event: 'refresh' },
            { id: 'descargar', text: 'Descargar', icon: 'bi bi-download', variant: 'success', event: 'download' }
          ]}]"
          :rightInfo="`Total: ${grupos.length} grupos`"
          @new="addGrupo"
          @refresh="loadGrupos"
          @download="downloadBulk"
        />
      </div>

      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando grupos...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>

        <div v-if="!loading && !error && (!grupos || grupos.length === 0)">
          <div v-if="!hasEmpresa" class="alert alert-warning">
            Debe elegir una empresa antes de poder continuar.
          </div>
          <div v-else class="alert alert-info">
            No hay grupos registrados. Haga clic en 'Nuevo' para agregar uno.
          </div>
        </div>

        <div v-if="!loading && !error && grupos && grupos.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="grupos"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'Clave', dir: 'asc' }"
            filter-placeholder="Buscar grupos..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <template #cell-EsActivo="{ value }">
              <span v-if="value" class="badge bg-success">Sí</span>
              <span v-else class="badge bg-secondary">No</span>
            </template>
            <template #actions="{ row }">
              <button @click="editGrupo(row)" class="btn btn-sm btn-outline-primary me-1" title="Editar grupo">
                <i class="fas fa-edit"></i>
              </button>
              <button @click="confirmDelete(row.Clave)" class="btn btn-sm btn-outline-danger" title="Eliminar grupo">
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>

    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <div class="modal fade" :class="{ 'show d-block': showModal }" id="grupoempleadoModal" tabindex="-1" aria-labelledby="grupoempleadoModalLabel" :aria-hidden="!showModal" @click.self="closeModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="grupoempleadoModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Grupo' : 'Nuevo Grupo' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <CatGrupoEmpleadoForm :grupo="formData" :isEditing="isEditing" :loading="modalLoading" @submit="handleSubmit" @cancel="closeModal" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import CatGrupoEmpleadoService from '../controllers/CatGrupoEmpleadoController';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import CatGrupoEmpleadoForm from '../components/CatGrupoEmpleadoForm.vue';

export default {
  name: 'CatGrupoEmpleadoView',
  components: { MyDataGrid, ToolbarComponent, CatGrupoEmpleadoForm },
  setup() {
    const { requiresAuth, requiresMenuPermission, canAccess, isLoading: accessLoading } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })
    return { requiresAuth, requiresMenuPermission, canAccess, accessLoading }
  },
  data() {
    return {
      service: new CatGrupoEmpleadoService(),
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        Clave: '',
        EmpresaId: 1,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true
      }
    };
  },
  computed: {
    grupos() { return this.service.grupos; },
    loading() { return this.service.loading; },
    error() { return this.service.error; },
    dataGridColumns() {
      return [
        { key: 'Clave', label: 'Clave', sortable: true },
        { key: 'EmpresaId', label: 'ID Empresa', sortable: true },
        { key: 'Nombre', label: 'Nombre', sortable: true },
        { key: 'ClaveErp', label: 'Clave ERP', sortable: true },
        { key: 'EsActivo', label: 'Activo', sortable: true, format: (v) => v ? 'Sí' : 'No' }
      ];
    }
  },
  created() { this.service.initialize(); },
  mounted() { this.$emit('toolbar-mounted'); },
  methods: {
    async loadGrupos() { await this.service.listar(); },
    addGrupo() { this.isEditing = false; this.openModal(); },
    async editGrupo(grupo) {
      this.isEditing = true;
      try {
        const response = await this.service.lectura(grupo.Clave);
        this.formData = response && response.data ? { ...response.data } : { ...grupo };
        this.openModal();
      } catch (e) {
        this.$alert.error('No se pudo cargar el grupo para edición.');
      }
    },
    confirmDelete(clave) {
      this.$alert.confirm(`¿Eliminar el grupo "${clave}"?`, 'Confirmar eliminación', { icon: 'warning' })
        .then((r) => { if (r.isConfirmed) this.deleteGrupo(clave); });
    },
    async deleteGrupo(clave) {
      try {
        const response = await this.service.borrar({ Clave: clave });
        if (response && response.success) {
          this.$alert.success(response.message || 'Grupo eliminado');
          this.service.recargar();
        } else {
          this.$alert.error(response.message || 'No se pudo eliminar el grupo');
        }
      } catch (e) {
        this.$alert.error('No se pudo eliminar el grupo');
      }
    },
    async downloadBulk() {
      try { await this.service.downloadBulk(); this.$alert.success('Descarga iniciada'); }
      catch (e) { this.$alert.error('Error al descargar: ' + e.message); }
    },
    openModal() {
      if (!this.isEditing) this.resetForm();
      this.showModal = true; document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false; this.modalLoading = false; this.isEditing = false; this.resetForm(); document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        Clave: '',
        EmpresaId: this.service.selectedEmpresa ? this.service.selectedEmpresa.EmpresaId : 1,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        const dataToSubmit = { ...formData };
        dataToSubmit.EmpresaId = parseInt(dataToSubmit.EmpresaId) || 1;
        const response = await this.service.guardar(dataToSubmit);
        if (response && response.success) {
          this.$alert.success(response.message || 'Grupo guardado');
          this.closeModal(); this.service.recargar();
        } else {
          this.$alert.error(response.message || 'No se pudo guardar');
        }
      } catch (e) {
        this.$alert.error('No se pudo guardar el grupo');
      } finally { this.modalLoading = false; }
    }
  }
}
</script>

<style scoped>
.cat-grupoempleado { min-height: calc(100vh - 200px); padding: 20px; }
.cat-grupoempleado-content { padding: 0; }
.page-content { padding: 1rem; }
.toolbar-container { margin-bottom: 1.5rem; }
.modal.show { display: block !important; }
.modal { padding: 0 !important; }
.modal-dialog { position: fixed !important; top: 50% !important; left: 50% !important; transform: translate(-50%, -50%) !important; margin: 0 !important; max-width: 600px; width: 90%; z-index: 1055; max-height: 90vh; overflow-y: auto; }
.modal.show .modal-dialog { transform: translate(-50%, -50%) !important; margin: 0 !important; }
@media (max-width: 768px) { .modal-dialog { width: 95% !important; max-width: none !important; max-height: 95vh; } }
@media (max-width: 576px) { .modal-dialog { width: 98% !important; max-height: 98vh; } }
</style>


